import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { OutcomeTaskService, ReferralService, sharedApiClient } from '../fetcher';
import { IdentityService, TeamService, getAssigneeName as resolveAssigneeName } from '@medprompt/shared';
import { saveDocumentNavigation } from '../detail/documentNavigationSession';

const usePatientActivity = ({ nhsId, patientId, patient, navigate, hasDocsRead, hasConsultRead, hasTaskRead, addNotificationRef, patientService }) => {
  const outcomeTaskManager = useMemo(() => new OutcomeTaskService(sharedApiClient), []);
  const referralManager = useMemo(() => new ReferralService(sharedApiClient), []);

  const [documents, setDocuments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loadingConsultations, setLoadingConsultations] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingReferrals, setLoadingReferrals] = useState(true);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  const filteredDocIdsRef = useRef([]);

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalLoading, setTaskModalLoading] = useState(false);

  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [referralModalLoading, setReferralModalLoading] = useState(false);

  const [consultationModalOpen, setConsultationModalOpen] = useState(false);
  const [selectedConsultationId, setSelectedConsultationId] = useState(null);

  useEffect(() => {
    if (!hasDocsRead) {
      setLoadingDocs(false);
      return;
    }

    const loadDocuments = async () => {
      setLoadingDocs(true);
      try {
        const data = await patientService.fetchPatientDocuments(nhsId);
        setDocuments(data);
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoadingDocs(false);
      }
    };
    loadDocuments();
  }, [nhsId, patientService, hasDocsRead]);

  useEffect(() => {
    if (!hasConsultRead) {
      setLoadingConsultations(false);
      return;
    }

    const loadConsultations = async () => {
      setLoadingConsultations(true);
      try {
        const data = await patientService.fetchPatientConsultations(nhsId);
        setConsultations(data);
      } catch (error) {
        console.error('Error loading consultations:', error);
      } finally {
        setLoadingConsultations(false);
      }
    };
    loadConsultations();
  }, [nhsId, patientService, hasConsultRead]);

  useEffect(() => {
    if (!hasTaskRead) {
      setLoadingTasks(false);
      return;
    }

    const loadTasks = async () => {
      setLoadingTasks(true);
      try {
        const data = await patientService.fetchPatientTasks(nhsId);
        setTasks(data);
      } catch (error) {
        console.error('Error loading tasks:', error);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, [nhsId, patientService, hasTaskRead]);

  useEffect(() => {
    if (!hasDocsRead) {
      setLoadingReferrals(false);
      return;
    }

    const loadReferrals = async () => {
      setLoadingReferrals(true);
      try {
        const data = await patientService.fetchPatientReferrals(nhsId);
        setReferrals(data);
      } catch (error) {
        console.error('Error loading referrals:', error);
      } finally {
        setLoadingReferrals(false);
      }
    };
    loadReferrals();
  }, [nhsId, patientService, hasDocsRead]);

  useEffect(() => {
    const loadUsersAndTeams = async () => {
      try {
        const identityManager = new IdentityService();
        const teamManager = new TeamService();

        const [users, teamsData] = await Promise.all([
          identityManager.fetchActiveClinicUsers(),
          teamManager.fetchTeams()
        ]);

        setActiveUsers(users || []);
        setTeams(teamsData || []);
      } catch (error) {
        console.error('Error loading users and teams:', error);
        setActiveUsers([]);
        setTeams([]);
      }
    };
    loadUsersAndTeams();
  }, []);

  const handleFilteredSortedDataChange = useCallback((filteredSortedDocs) => {
    if (!Array.isArray(filteredSortedDocs)) return;
    filteredDocIdsRef.current = filteredSortedDocs.map(doc => doc.documentId);
  }, []);

  const handleDocumentClick = useCallback((documentId) => {
    saveDocumentNavigation(filteredDocIdsRef.current, documentId);

    navigate(`/document-detail/${documentId}`, {
      state: {
        fromPatient: true,
        patientNhsId: nhsId,
        patientId,
        patientName: patient?.name
      }
    });
  }, [navigate, nhsId, patientId, patient]);

  const getAssigneeName = useCallback((assigneeId) => {
    return resolveAssigneeName(assigneeId, activeUsers, teams);
  }, [activeUsers, teams]);

  const handleTaskClick = useCallback((task) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
  }, []);

  const handleTaskSave = useCallback(async (update) => {
    setTaskModalLoading(true);
    try {
      await outcomeTaskManager.updateTask(update);
      const updatedTasks = await patientService.fetchPatientTasks(nhsId);
      setTasks(updatedTasks);
      const updatedTask = updatedTasks.find(t => t.taskId === update.taskId);
      if (updatedTask) {
        setSelectedTask(updatedTask);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      addNotificationRef.current('task-update-error', 'Failed to update task', { isError: true });
    } finally {
      setTaskModalLoading(false);
    }
  }, [outcomeTaskManager, patientService, nhsId, addNotificationRef]);

  const handleTaskMarkComplete = useCallback(async (update) => {
    setTaskModalLoading(true);
    try {
      await outcomeTaskManager.completeTask(update);
      setTaskModalOpen(false);
      setSelectedTask(null);
      const updatedTasks = await patientService.fetchPatientTasks(nhsId);
      setTasks(updatedTasks);
      addNotificationRef.current('task-complete-success', 'Task marked as complete');
    } catch (error) {
      console.error('Error completing task:', error);
      addNotificationRef.current('task-complete-error', 'Failed to mark task as complete', { isError: true });
    } finally {
      setTaskModalLoading(false);
    }
  }, [outcomeTaskManager, patientService, nhsId, addNotificationRef]);

  const handleTaskDelete = useCallback(async (taskId) => {
    setTaskModalLoading(true);
    try {
      await outcomeTaskManager.deleteTask(taskId);
      setTaskModalOpen(false);
      setSelectedTask(null);
      const updatedTasks = await patientService.fetchPatientTasks(nhsId);
      setTasks(updatedTasks);
      addNotificationRef.current('task-delete-success', 'Task deleted');
    } catch (error) {
      console.error('Error deleting task:', error);
      addNotificationRef.current('task-delete-error', 'Failed to delete task', { isError: true });
    } finally {
      setTaskModalLoading(false);
    }
  }, [outcomeTaskManager, patientService, nhsId, addNotificationRef]);

  const handleReferralClick = useCallback((referral) => {
    setSelectedReferral(referral);
    setReferralModalOpen(true);
  }, []);

  const handleConsultationClick = useCallback((consultation) => {
    setSelectedConsultationId(consultation.consultationId);
    setConsultationModalOpen(true);
  }, []);

  const handleReferralMarkComplete = useCallback(async (referralId) => {
    setReferralModalLoading(true);
    try {
      await referralManager.completeReferral(referralId);
      setReferralModalOpen(false);
      setSelectedReferral(null);
      const updatedReferrals = await patientService.fetchPatientReferrals(nhsId);
      setReferrals(updatedReferrals);
      addNotificationRef.current('referral-complete-success', 'Referral marked as complete');
    } catch (error) {
      console.error('Error completing referral:', error);
      addNotificationRef.current('referral-complete-error', 'Failed to mark referral as complete', { isError: true });
    } finally {
      setReferralModalLoading(false);
    }
  }, [referralManager, patientService, nhsId, addNotificationRef]);

  const handleReferralUpdate = useCallback(async (update) => {
    setReferralModalLoading(true);
    try {
      await referralManager.updateReferral(update);
      const updatedReferrals = await patientService.fetchPatientReferrals(nhsId);
      setReferrals(updatedReferrals);
      const updatedReferral = updatedReferrals.find(r => r.referralId === update.referralId);
      if (updatedReferral) {
        setSelectedReferral(updatedReferral);
      }
    } catch (error) {
      console.error('Error updating referral:', error);
      addNotificationRef.current('referral-update-error', 'Failed to update referral', { isError: true });
    } finally {
      setReferralModalLoading(false);
    }
  }, [referralManager, patientService, nhsId, addNotificationRef]);

  const handleReferralDelete = useCallback(async (referralId) => {
    setReferralModalLoading(true);
    try {
      await referralManager.deleteReferral(referralId);
      setReferralModalOpen(false);
      setSelectedReferral(null);
      const updatedReferrals = await patientService.fetchPatientReferrals(nhsId);
      setReferrals(updatedReferrals);
      addNotificationRef.current('referral-delete-success', 'Referral deleted');
    } catch (error) {
      console.error('Error deleting referral:', error);
      addNotificationRef.current('referral-delete-error', 'Failed to delete referral', { isError: true });
    } finally {
      setReferralModalLoading(false);
    }
  }, [referralManager, patientService, nhsId, addNotificationRef]);

  const closeTask = () => {
    setTaskModalOpen(false);
    setSelectedTask(null);
  };

  const closeReferral = () => {
    setReferralModalOpen(false);
    setSelectedReferral(null);
  };

  const closeConsultation = () => {
    setConsultationModalOpen(false);
    setSelectedConsultationId(null);
  };

  return {
    documents,
    consultations,
    tasks,
    referrals,
    loadingDocs,
    loadingConsultations,
    loadingTasks,
    loadingReferrals,
    activeUsers,
    teams,
    getAssigneeName,
    handleFilteredSortedDataChange,
    handleDocumentClick,
    handleTaskClick,
    handleTaskSave,
    handleTaskMarkComplete,
    handleTaskDelete,
    taskModalOpen,
    selectedTask,
    taskModalLoading,
    closeTask,
    handleReferralClick,
    handleReferralMarkComplete,
    handleReferralUpdate,
    handleReferralDelete,
    referralModalOpen,
    selectedReferral,
    referralModalLoading,
    closeReferral,
    handleConsultationClick,
    consultationModalOpen,
    selectedConsultationId,
    closeConsultation,
  };
};

export default usePatientActivity;
